'use client';

import type { CSSProperties } from 'react';
import { motion } from 'framer-motion';
import { stegaClean } from '@sanity/client/stega';

import { GroqImage } from '@/helpers/Image';
import type { GROQImage } from '@/sanity/lib/definitions';

const ease = [0.25, 0.1, 0.25, 1] as const;

// Written out rather than interpolated: Tailwind only sees literal class names.
const COL_SPAN: Record<number, string> = {
  1: 'lg:col-span-1',
  2: 'lg:col-span-2',
  3: 'lg:col-span-3',
  4: 'lg:col-span-4',
  5: 'lg:col-span-5',
  6: 'lg:col-span-6',
  7: 'lg:col-span-7',
  8: 'lg:col-span-8',
  9: 'lg:col-span-9',
  10: 'lg:col-span-10',
  11: 'lg:col-span-11',
  12: 'lg:col-span-12',
};
const ROW_SPAN: Record<number, string> = {
  1: 'lg:row-span-1',
  2: 'lg:row-span-2',
};

export interface SanityHotspot {
  x: number;
  y: number;
  width?: number;
  height?: number;
}

const focus = (h?: SanityHotspot) =>
  h ? `${Math.round(h.x * 1000) / 10}% ${Math.round(h.y * 1000) / 10}%` : '50% 50%';

export interface MasonryGalleryItem {
  _key?: string;
  value?: string;
  label?: string;
  caption?: string;
  image?: GROQImage;
  mobileImage?: GROQImage;
  imageHotspot?: SanityHotspot;
  mobileImageHotspot?: SanityHotspot;
  /**
   * Optional full-bleed backdrop photo (desktop only), blended into the tile's
   * `bgColor` with `mix-blend-mode: luminosity` at 40% opacity — the photo
   * contributes only its light/dark detail, the tile color supplies the hue.
   * This is how the faded painting behind the TÜV tile is built in the design.
   */
  bgImage?: GROQImage;
  theme?: 'dark' | 'light';
  /** How many grid columns this tile takes (desktop only), out of however many `columns` defines. */
  colSpan?: number;
  /** 1 or 2 grid rows (desktop only). */
  rowSpan?: number;
  /**
   * When true, `image` renders as a small, centered, inline block (natural
   * aspect ratio, not stretched) with `label`/`value`/`caption` stacked below
   * it — for tiles like a certification seal that sit on a visible background
   * color rather than a full-bleed photo. When false/omitted (the default),
   * `image` is absolute-positioned, `object-cover`, filling the whole tile,
   * with text overlaid at the bottom.
   */
  imageInline?: boolean;
  /**
   * Selects a structural layout other than the default (full-bleed `image`,
   * text overlaid at the bottom) or `imageInline`. `'mission'`: serif italic
   * title (`value`, else `label`) + `caption` pill at the bottom-left, `decorativeImage` bleeding off
   * the tile's right/top edge. `'payout'`: `value`/`label` at the TOP,
   * `graphicImage` stretched across the bottom.
   */
  contentLayout?: 'default' | 'mission' | 'payout';
  /** Oversized decorative image for `contentLayout: 'mission'` — bleeds off the tile's edges. */
  decorativeImage?: GROQImage;
  /** Chart/graphic image for `contentLayout: 'payout'`, stretched to the tile's width below the text. */
  graphicImage?: GROQImage;
  /** `'small'` renders `value` as a name/heading (32px) instead of a stat figure (48px). */
  valueSize?: 'default' | 'small';
  /** Desktop only: 24px inset and a tighter value/label gap instead of 32px — the design's map tile. */
  compactText?: boolean;
  /**
   * Exact background color for this tile, e.g. "#121212". Each tile in the
   * source design has its own shade — falls back to `#04271d`/`#f4f5f5` (by
   * `theme`) when not set.
   */
  bgColor?: string;
  /** Background below the desktop breakpoint, when the mobile design differs. Any CSS background (color or gradient). */
  mobileBgColor?: string;
  /**
   * Tile height in the 398px-wide mobile design, in px. The tile keeps that
   * proportion at any mobile width. Falls back to the mobile image's
   * proportions, then to the layout's default.
   */
  mobileHeight?: number;
  /** Overlays the halftone-dot texture on the tile's background (desktop only), as in the design. */
  showDotsTexture?: boolean;
}

// Cap the stagger so a large item list doesn't push the last tile's entrance
// noticeably late; beyond this many tiles they all catch up to the same delay.
const MAX_STAGGER_INDEX = 5;
const STAGGER_STEP = 0.08;

const DOTS_TEXTURE_URL = '/careers/stat-tile-dots.png';

// Width of the mobile design frame every `mobileHeight` is measured against.
const MOBILE_DESIGN_WIDTH = 398;
const MOBILE_DEFAULT_HEIGHT: Record<string, number> = { mission: 269, payout: 285, default: 289, tall: 400 };

function MasonryTile({ item: rawItem, index }: { item: MasonryGalleryItem; index: number }) {
  // Strings used as values (comparisons, CSS) drop the draft-mode stega
  // characters; displayed text keeps them for click-to-edit.
  const item: MasonryGalleryItem = {
    ...rawItem,
    ...stegaClean({
      theme: rawItem.theme,
      contentLayout: rawItem.contentLayout,
      valueSize: rawItem.valueSize,
      bgColor: rawItem.bgColor,
      mobileBgColor: rawItem.mobileBgColor,
    }),
  };
  const dark = (item.theme ?? 'dark') !== 'light';
  const layout = item.contentLayout ?? 'default';
  const mobileHeight =
    item.mobileHeight ??
    (item.mobileImage?.width && item.mobileImage?.height
      ? (MOBILE_DESIGN_WIDTH * item.mobileImage.height) / item.mobileImage.width
      : layout !== 'default'
        ? MOBILE_DEFAULT_HEIGHT[layout]
        : item.rowSpan === 2
          ? MOBILE_DEFAULT_HEIGHT.tall
          : MOBILE_DEFAULT_HEIGHT.default);
  const bgColor = item.bgColor ?? (dark ? '#04271d' : '#f4f5f5');
  const style = {
    '--m-ar': `${MOBILE_DESIGN_WIDTH} / ${Math.round(mobileHeight * 100) / 100}`,
    '--focus': focus(item.imageHotspot),
    '--m-focus': focus(item.mobileImageHotspot),
    '--bg': bgColor,
    '--m-bg': item.mobileBgColor ?? bgColor,
  } as CSSProperties;

  const tileClass = `relative isolate overflow-hidden [background:var(--m-bg)] max-lg:aspect-[var(--m-ar)] max-lg:rounded-[1.25rem] lg:[background:var(--bg)] ${
    COL_SPAN[item.colSpan ?? 12] ?? COL_SPAN[12]
  } ${ROW_SPAN[item.rowSpan ?? 1] ?? ROW_SPAN[1]}`;

  const motionProps = {
    initial: { opacity: 0, y: 32 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.3 },
    transition: { duration: 0.6, ease, delay: Math.min(index, MAX_STAGGER_INDEX) * STAGGER_STEP },
    style,
  };

  const dotsTexture = item.showDotsTexture && (
    <div
      className="pointer-events-none absolute inset-0 opacity-20 mix-blend-exclusion max-lg:hidden"
      aria-hidden="true"
      style={{
        backgroundImage: `url(${DOTS_TEXTURE_URL})`,
        backgroundSize: '120.3rem auto',
        backgroundPosition: '-3rem -85.9rem',
      }}
    />
  );

  const bgImageLayer = item.bgImage && (
    <div
      className="pointer-events-none absolute inset-0 opacity-40 mix-blend-luminosity max-lg:hidden"
      aria-hidden="true"
    >
      <GroqImage src={item.bgImage} alt="" quality={100} className="!absolute !inset-0 !h-full !w-full !object-cover" />
    </div>
  );

  const valueClass = `mb-0 font-medium ${dark ? 'text-white' : 'text-black'} ${
    item.valueSize === 'small'
      ? 'text-[3.5rem] leading-[1.12] tracking-[-0.04em] lg:text-[4rem]'
      : `text-[6rem] leading-[1.2] tracking-[-0.01em] ${item.compactText ? 'lg:leading-[0.9]' : ''}`
  }`;
  const labelClass = `mb-0 text-[2.25rem] leading-[1.5] tracking-[-0.01em] ${dark ? 'text-white/70' : 'text-black/70'}`;

  const textBlock = (item.value || item.label || item.caption) && (
    <div className={`relative z-10 flex flex-col ${item.valueSize === 'small' ? 'gap-[0.75rem] lg:gap-[1.25rem]' : 'gap-[0.625rem]'}`}>
      {item.value && <p className={valueClass}>{item.value}</p>}
      {item.label && <p className={labelClass}>{item.label}</p>}
      {item.caption && (
        <span
          className={`mt-[1rem] w-fit rounded-full px-[2rem] py-[0.75rem] font-geist-mono text-[1.5rem] uppercase ${
            dark ? 'bg-white/10 text-white/80' : 'bg-black/5 text-black/70'
          }`}
        >
          {item.caption}
        </span>
      )}
    </div>
  );

  if (layout === 'mission') {
    // Figma "Our Mission" tile: the statue is placed in % of the tile so it
    // bleeds off the top/right edge by the same proportion at any size; the
    // serif title + pill stack sits bottom-left, independent of the image.
    return (
      <motion.div {...motionProps} className={`${tileClass} flex flex-col justify-end`}>
        {item.decorativeImage && (
          <GroqImage
            src={item.decorativeImage}
            alt=""
            quality={100}
            aria-hidden="true"
            className="pointer-events-none !absolute !left-[35.68%] !top-[-7.43%] !h-[107.55%] !w-[93.2%] !max-w-none !object-fill lg:!left-[34.9%] lg:!top-[-13.67%] lg:!h-[163.33%] lg:!w-[87.01%]"
          />
        )}
        <div className="relative z-10 flex flex-col items-start gap-[1.875rem] pb-[3rem] pl-[1.5rem] lg:gap-[5.575rem] lg:pb-[4rem] lg:pl-[2.875rem]">
          {(item.value || item.label) && (
            <p
              className={`mb-0 font-blacker text-[3.5rem] italic leading-[0.83] tracking-[-0.07em] opacity-90 lg:max-w-[33.25rem] lg:text-[7rem] lg:leading-[0.95] lg:tracking-[-0.01em] lg:opacity-100 ${
                dark ? 'text-white' : 'text-black'
              }`}
            >
              {item.value || item.label}
            </p>
          )}
          {item.caption && (
            <span
              className={`w-fit rounded-full px-[2rem] py-[0.625rem] font-blacker text-[2rem] italic leading-[1.5] tracking-[-0.04em] backdrop-blur-[8.75rem] ${
                dark ? 'bg-white/10 text-white/80' : 'bg-black/5 text-black/70'
              }`}
            >
              {item.caption}
            </span>
          )}
        </div>
      </motion.div>
    );
  }

  if (layout === 'payout') {
    // Figma "$3.5 mi+" tile: value/label at the TOP, the chart stretched across
    // the bottom with the design's side insets (bars stay evenly spread, as in
    // both the desktop and the wider mobile chart).
    return (
      <motion.div
        {...motionProps}
        className={`${tileClass} px-[2.75rem] pt-[4.5rem] lg:px-[3.625rem] lg:pt-[4.125rem]`}
      >
        <div className="relative z-10 flex flex-col">
          {item.value && (
            <p
              className={`mb-0 text-[4rem] font-medium leading-[0.72] tracking-[-0.01em] lg:text-[6rem] lg:leading-[0.875] ${
                dark ? 'text-white' : 'text-black'
              }`}
            >
              {item.value}
            </p>
          )}
          {item.label && <p className={`${labelClass} mt-[0.5rem]`}>{item.label}</p>}
        </div>
        {item.graphicImage && (
          <GroqImage
            src={item.graphicImage}
            alt=""
            quality={100}
            aria-hidden="true"
            className="pointer-events-none !absolute !bottom-[3.08rem] !left-[6.03%] !h-[19.92rem] !w-[87.94%] !max-w-none !object-fill lg:!left-[9.68%] lg:!w-[83.23%]"
          />
        )}
      </motion.div>
    );
  }

  if (item.imageInline) {
    // Figma TÜV tile: the seal sits small and centered on the tile's color,
    // with the label below it. Desktop anchors the stack 78px from the top;
    // mobile centers it.
    return (
      <motion.div
        {...motionProps}
        className={`${tileClass} flex flex-col items-center justify-center gap-[2.45rem] lg:justify-start lg:gap-[3.2rem] lg:pt-[9.7rem]`}
      >
        {dotsTexture}
        {bgImageLayer}
        {item.image && (
          <GroqImage
            src={item.image}
            alt=""
            quality={100}
            className="relative z-10 !h-[14.95rem] !w-[41.93rem] max-w-full !object-fill"
          />
        )}
        {textBlock}
      </motion.div>
    );
  }

  return (
    <motion.div
      {...motionProps}
      className={`${tileClass} flex flex-col justify-end p-[2.5rem] ${
        item.compactText
          ? 'lg:p-[3rem] lg:pb-[3.125rem]'
          : item.valueSize === 'small'
            ? 'lg:p-[4rem] lg:pb-[4.125rem]'
            : 'lg:p-[4rem] lg:pb-[3.875rem]'
      }`}
    >
      {dotsTexture}
      {bgImageLayer}
      {item.image && (
        <GroqImage
          src={item.image}
          alt=""
          quality={100}
          aria-hidden="true"
          className={`pointer-events-none !absolute !inset-0 !h-full !w-full !object-cover object-[var(--focus)] ${
            item.mobileImage ? 'max-lg:!hidden' : ''
          }`}
        />
      )}
      {item.mobileImage && (
        <GroqImage
          src={item.mobileImage}
          alt=""
          quality={100}
          aria-hidden="true"
          className="pointer-events-none !absolute !inset-0 !h-full !w-full !object-cover object-[var(--m-focus)] lg:!hidden"
        />
      )}

      {textBlock}
    </motion.div>
  );
}

export interface MasonryGalleryProps {
  items: MasonryGalleryItem[];
  /**
   * Desktop row heights, e.g. `['36.125rem', '37.5rem', '35.625rem']`. Required
   * so the grid's row track sizes stay page-specific instead of hardcoded here —
   * this component has no opinion on how many rem a given page's tiles need.
   */
  rowHeights: [string, string, string];
  /**
   * Desktop `grid-template-columns`. Defaults to 12 equal columns; pass
   * unequal tracks (e.g. `'repeat(5, 92fr) repeat(7, 87fr)'`) when the
   * design's tile widths don't fall on equal columns. Each item's `colSpan`
   * counts these tracks.
   */
  columns?: string;
  className?: string;
}

/**
 * A CSS-Grid tile layout where each item declares its own `colSpan`/`rowSpan`
 * (up to 2 rows) — the "masonry" here means the grid adapts to however many
 * items exist, not that items auto-flow into gaps. Sizing is driven by whoever
 * edits the item list, matched against the source design (Figma).
 *
 * Deliberately NOT a true auto-flowing masonry (react-masonry-css, CSS
 * `columns`, etc.) — those reflow tiles by their own heuristic, which would
 * not reproduce a hand-designed layout with deliberately uneven tile sizes.
 */
export default function MasonryGallery({
  items,
  rowHeights,
  columns = 'repeat(12, minmax(0, 1fr))',
  className = '',
}: MasonryGalleryProps) {
  if (items.length === 0) return null;

  return (
    <div
      className={`grid grid-cols-1 gap-[1.25rem] lg:gap-[2rem] ${className}`}
      style={
        {
          '--row-1': rowHeights[0],
          '--row-2': rowHeights[1],
          '--row-3': rowHeights[2],
          '--cols': columns,
        } as CSSProperties
      }
    >
      <style jsx>{`
        @media (min-width: 993px) {
          .grid {
            grid-template-columns: var(--cols);
            grid-template-rows: var(--row-1) var(--row-2) var(--row-3);
          }
        }
      `}</style>
      {items.map((item, index) => (
        <MasonryTile key={item._key ?? index} item={item} index={index} />
      ))}
    </div>
  );
}
